import React from 'react';
import { T, RADIUS } from './messagesModel';
import { albumLayout, albumStatus, fmtDur } from './albumModel';

/**
 * An album in the thread (handoff §3): no bubble chrome — the grid is its own frame. 320px
 * wide, 20px corners, a twelve-column grid with 3px gaps the thread's background shows
 * through. Cover, then a row of up to three, then a row of up to two; past six items the
 * last tile carries +N. Tapping a tile opens the album viewer at that item.
 *
 * While the sender's copy is still going up, each tile wears its own state from the outbox
 * (`items`): WAITING, a spinner with its percentage and a progress bar, PROCESSING, or the
 * failed overlay whose tap retries that one item.
 */
export default function AlbumGrid({ msg, items = null, onOpen, onRetryItem, inFlight = false }) {
  const albumItems = msg.albumItems || [];
  const n = albumItems.length;
  if (!n) return null;
  const stateOf = (item) => items?.find((i) => i.clientId === item.id) || null;
  return (
    <div
      data-testid="album-grid"
      data-count={n}
      style={{ width: 320, maxWidth: '100%', borderRadius: RADIUS.bubble, overflow: 'hidden', display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 3 }}
    >
      {albumLayout(n).map((tile, k) => {
        const item = albumItems[tile.index];
        if (!item) return null;
        const st = stateOf(item);
        const state = st?.state || (item.status === 'encoding' ? 'processing' : item.status === 'failed' ? 'failed' : 'ready');
        const busy = state === 'uploading' || state === 'processing';
        const queued = state === 'queued';
        const failed = state === 'failed';
        const dim = busy || queued;
        const more = tile.more;
        const isVideo = item.kind === 'video';
        const thumb = item.thumbnailUrl || (isVideo ? null : item.url);
        const label = failed && !more ? `Retry ${item.filename || 'item'}` : `Open ${tile.index + 1} of ${n}`;
        return (
          <button
            key={item.id || k}
            type="button"
            data-testid="album-tile"
            data-state={state}
            aria-label={label}
            onClick={(e) => { e.stopPropagation(); if (failed && !more && st) onRetryItem?.(item.id); else onOpen?.(tile.index); }}
            style={{ position: 'relative', gridColumn: `span ${tile.span}`, aspectRatio: tile.ratio, padding: 0, border: 0, background: '#000', overflow: 'hidden', display: 'block', cursor: 'pointer' }}
          >
            {thumb
              ? <img src={thumb} alt="" draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: dim ? 0.55 : 1, transition: 'opacity 200ms' }} />
              : isVideo && item.url && item.url.startsWith('blob:')
                // eslint-disable-next-line jsx-a11y/media-has-caption
                ? <video src={item.url} muted playsInline preload="metadata" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: dim ? 0.55 : 1 }} />
                : <span style={{ position: 'absolute', inset: 0, background: '#1A1A1A' }} />}
            {isVideo && state === 'ready' && !more && (
              <span className="font-mono font-bold" style={{ position: 'absolute', left: 8, bottom: 7, display: 'flex', alignItems: 'center', gap: 5, fontSize: 9, letterSpacing: '.12em', color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,.6)' }}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="#fff"><path d="M6 3l15 9-15 9z" /></svg>{item.durationSec ? fmtDur(item.durationSec) : ''}
              </span>
            )}
            {busy && !more && (
              <>
                <span className="font-mono font-bold" style={{ position: 'absolute', left: 8, bottom: 8, display: 'flex', alignItems: 'center', gap: 5, fontSize: 9, letterSpacing: '.12em', color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,.6)' }}>
                  <span aria-hidden className="animate-spin" style={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid #fff', borderTopColor: 'transparent', boxSizing: 'border-box' }} />
                  {state === 'uploading' ? `${Math.round(st?.progress || 0)}%` : 'PROCESSING'}
                </span>
                <span style={{ position: 'absolute', left: 0, bottom: 0, height: 3, background: '#fff', width: `${state === 'uploading' ? Math.round(st?.progress || 0) : 100}%`, transition: 'width 150ms linear' }} />
              </>
            )}
            {queued && !more && (
              <span className="font-mono font-bold" style={{ position: 'absolute', left: 8, bottom: 8, fontSize: 9, letterSpacing: '.12em', color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,.6)' }}>WAITING</span>
            )}
            {failed && !more && (
              <span style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#fff' }}>
                <span className="font-mono font-bold" style={{ width: 22, height: 22, borderRadius: '50%', background: T.danger, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>!</span>
                <span className="font-mono font-bold" style={{ fontSize: 8, letterSpacing: '.12em' }}>{st ? 'TAP TO RETRY' : 'DIDN’T PROCESS'}</span>
              </span>
            )}
            {more > 0 && (
              <span className="font-mono font-bold" style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18, letterSpacing: '.06em' }}>+{more}</span>
            )}
            {tile.index === 0 && n > 1 && (
              <span className="font-mono font-bold" style={{ position: 'absolute', top: 8, right: 8, height: 22, padding: '0 9px', borderRadius: RADIUS.pill, background: 'rgba(0,0,0,.72)', color: '#fff', display: 'flex', alignItems: 'center', fontSize: 9, letterSpacing: '.12em' }}>{n} ITEMS</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The aggregate line under a sending album (handoff §2), right-aligned under the grid:
 * SENDING · 2 OF 5 · 48% → 1 OF 5 DIDN’T SEND with RETRY / SEND WITHOUT IT → PROCESSING
 * VIDEO… → gone. Extends OutboxStatus rather than standing beside it.
 */
export function AlbumStatus({ items, onRetry, onDropFailed }) {
  const st = albumStatus(items);
  if (!st) return null;
  const pill = { borderRadius: RADIUS.pill, border: `2px solid ${T.danger}`, background: 'transparent', color: T.danger, fontSize: 9, fontWeight: 700, letterSpacing: '.1em', padding: '4px 10px', cursor: 'pointer' };
  return (
    <div data-testid="album-status" data-kind={st.kind} className="font-mono font-bold flex items-center justify-end flex-wrap" style={{ marginTop: 3, gap: 8, fontSize: 9, letterSpacing: '.12em', color: st.danger ? T.danger : T.grey500 }}>
      {st.spin && <span aria-hidden className="animate-spin" style={{ width: 10, height: 10, borderRadius: '50%', border: `2px solid ${T.grey400}`, borderTopColor: 'transparent', boxSizing: 'border-box' }} />}
      <span>{st.text}</span>
      {st.kind === 'failed' && (
        <>
          <button type="button" onClick={onRetry} style={pill}>RETRY</button>
          <button type="button" onClick={onDropFailed} style={pill}>SEND WITHOUT IT</button>
        </>
      )}
    </div>
  );
}
